if (!customElements.get('rendi-choice-group')) {
  class RendiChoiceGroup extends HTMLElement {
    constructor() {
      super();
      this.handleChangeBound = this.handleChange.bind(this);
    }

    connectedCallback() {
      this.name = this.dataset.name;
      this.cast = this.dataset.cast || 'string';
      this.fieldset = this.querySelector('fieldset');
      this.inputs = Array.from(this.querySelectorAll('input[type="radio"], input[type="checkbox"]'));
      this.labels = Array.from(this.querySelectorAll('[data-choice-label]'));
      // Multi-select when the group is built from checkboxes; single-select
      // (radio) otherwise. Detected from the markup so existing radio groups
      // keep their exact behaviour — this is a purely additive extension.
      this.multiple = this.inputs.some((input) => input.type === 'checkbox');

      if (this.fieldset && !this.fieldset.hasAttribute('tabindex')) {
        this.fieldset.setAttribute('tabindex', '-1');
      }

      this.inputs.forEach((input) => {
        input.addEventListener('change', this.handleChangeBound);
      });

      this.updateUi();
    }

    disconnectedCallback() {
      this.inputs?.forEach((input) => {
        input.removeEventListener('change', this.handleChangeBound);
      });
    }

    handleChange(event) {
      this.updateUi();
      this.dispatchValue(event.target.value, event.target.checked);
    }

    parseValue(value) {
      if (this.cast === 'boolean') {
        return value === 'true';
      }

      return value;
    }

    // All currently checked values, parsed. Single-select groups return a
    // 0- or 1-length array; multi-select groups return every checked value.
    getValues() {
      return this.inputs
        .filter((input) => input.checked)
        .map((input) => this.parseValue(input.value));
    }

    dispatchValue(value, checked) {
      this.dispatchEvent(new CustomEvent('rendi:field-change', {
        bubbles: true,
        detail: {
          name: this.name,
          value: this.parseValue(value),
          checked: Boolean(checked),
          values: this.getValues()
        }
      }));
    }

    updateUi() {
      this.labels.forEach((label) => {
        const input = label.querySelector('input');
        const isSelected = Boolean(input?.checked);
        label.classList.toggle('is-selected', isSelected);
      });
    }

    focus() {
      this.fieldset?.focus();
    }

    // Single-select setter (radio groups). Unchanged contract for existing
    // consumers: checks the one matching input and, unless silent, dispatches.
    setValue(value, options = {}) {
      const normalizedValue = this.cast === 'boolean' ? String(Boolean(value)) : String(value);
      let didUpdate = false;

      this.inputs.forEach((input) => {
        const matches = input.value === normalizedValue;
        input.checked = matches;
        didUpdate = didUpdate || matches;
      });

      this.updateUi();

      if (didUpdate && !options.silent) {
        this.dispatchValue(normalizedValue, true);
      }
    }

    // Multi-select setter: check exactly the inputs whose value is in `values`,
    // uncheck the rest. Used to mirror one instance's state onto the sibling
    // instances of the same dimension (compact card / drawer / desktop bar).
    setValues(values, options = {}) {
      const wanted = (values || []).map((value) => (
        this.cast === 'boolean' ? String(Boolean(value)) : String(value)
      ));

      this.inputs.forEach((input) => {
        input.checked = wanted.indexOf(input.value) !== -1;
      });

      this.updateUi();

      if (!options.silent) {
        this.dispatchValue('', false);
      }
    }

    clear(options = {}) {
      this.inputs.forEach((input) => {
        input.checked = false;
      });

      this.updateUi();

      if (!options.silent) {
        this.dispatchValue('', false);
      }
    }
  }

  customElements.define('rendi-choice-group', RendiChoiceGroup);
}
